import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Downshift, { useCombobox } from 'downshift';
import classNames from 'classnames';
import * as KeyCode from 'keycode-js';
import './styles.css';
import { ReactComponent as DownArrowIcon } from './up-arrow-icon.svg';
import { ReactComponent as UpArrowIcon } from './down-arrow-icon.svg';
import { ReactComponent as OptionRemoveIcon } from './option-remove-icon.svg';

const Select = ({ id, data, onOptionAdd, onOptionRemove }) => {
    const {
        getInputProps,
        getToggleButtonProps,
        getComboboxProps,
        getMenuProps,
        isOpen,
        openMenu,
    } = useCombobox({ items: data });

    const [inputValue, setInputValue] = useState('');

    const placeholder = useMemo(() => {
        return isOpen ? '' : data.map(item => item).join(', ');
    }, [isOpen, data]);

    const handleOptionRemove = useCallback(
        value => e => {
            e.preventDefault();
            onOptionRemove && onOptionRemove(value, id);
        },
        [id, onOptionRemove]
    );

    const handleInputKeyDown = useCallback(
        event => {
            switch (event.keyCode) {
                case KeyCode.KEY_COMMA:
                case KeyCode.KEY_RETURN:
                case KeyCode.KEY_TAB: {
                    event.preventDefault();
                    if (onOptionAdd) {
                        onOptionAdd(inputValue, id);
                    }

                    setInputValue('');
                    break;
                }
            }
        },
        [id, inputValue, onOptionAdd]
    );

    const handleInputChange = useCallback(e => {
        setInputValue(e.target.value);
    }, []);

    const handleInputFocus = useCallback(() => {
        if (!isOpen) {
            openMenu();
        }
    }, [isOpen, openMenu]);

    return (
        <>
            <div
                {...getComboboxProps({
                    className: 'validate-select',
                })}
            >
                <div className="validate-select__input-container">
                    <input
                        {...getInputProps({
                            value: inputValue,
                            placeholder: placeholder,
                            className: classNames('validate-select__input', {
                                'validate-select__input--opened': isOpen,
                            }),
                            onFocus: handleInputFocus,
                            onChange: handleInputChange,
                            onKeyDown: handleInputKeyDown,
                        })}
                    />
                    <button
                        {...getToggleButtonProps({
                            className: classNames('validate-select__input-button', {
                                'validate-select__input-button--opened': isOpen,
                            }),
                        })}
                    >
                        <div>{isOpen ? <DownArrowIcon /> : <UpArrowIcon />}</div>
                    </button>
                </div>
                <ul
                    {...getMenuProps({
                        className: classNames({
                            'validate-select__options-container': isOpen,
                        }),
                    })}
                >
                    {isOpen &&
                        data.map(item => (
                            <li key={item} className="validate-select__option">
                                <span>{item}</span>
                                <div
                                    className="validate-select__option-remove"
                                    onClick={handleOptionRemove(item)}
                                >
                                    <OptionRemoveIcon />
                                </div>
                            </li>
                        ))}
                </ul>
            </div>
        </>
    );
};

Select.propTypes = {
    id: PropTypes.string.isRequired,
    data: PropTypes.array.isRequired,
    onOptionAdd: PropTypes.func.isRequired,
    onOptionRemove: PropTypes.func.isRequired,
};

export default React.memo(Select);
